import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { contactsAPI } from '../lib/api';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Mail, Phone, Building2, Briefcase,
  TrendingUp, FileText, Receipt, Star, Activity, ShieldAlert
} from 'lucide-react';
import { getInitials, getAvatarColor } from '../lib/utils';
import { toast } from 'sonner';

function KpiCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-bold text-foreground text-sm truncate">{value}</p>
      </div>
    </div>
  );
}

function engagementColor(score) {
  if (score === 'High') return 'text-emerald-600';
  if (score === 'Medium') return 'text-amber-600';
  return 'text-red-600';
}

function churnColor(risk) {
  if (risk === 'Minimal') return 'text-emerald-600';
  if (risk === 'Low') return 'text-blue-600';
  if (risk === 'Medium') return 'text-amber-600';
  return 'text-red-600';
}

function loyaltyBadge(loyalty) {
  const map = {
    VIP: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    Gold: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    Standard: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  };
  return map[loyalty] || map.Standard;
}

function invoiceStatusBadge(status) {
  const map = {
    paid: 'bg-emerald-100 text-emerald-700',
    sent: 'bg-blue-100 text-blue-700',
    draft: 'bg-slate-100 text-slate-600',
    overdue: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  return map[status] || map.draft;
}

function quoteStatusBadge(status) {
  const map = {
    accepted: 'bg-emerald-100 text-emerald-700',
    sent: 'bg-blue-100 text-blue-700',
    draft: 'bg-slate-100 text-slate-600',
    rejected: 'bg-red-100 text-red-700',
  };
  return map[status] || map.draft;
}

function opportunityStageColor(stage) {
  const map = {
    lead: 'bg-slate-100 text-slate-600',
    qualified: 'bg-blue-100 text-blue-700',
    proposal: 'bg-violet-100 text-violet-700',
    negotiation: 'bg-amber-100 text-amber-700',
    won: 'bg-emerald-100 text-emerald-700',
    lost: 'bg-red-100 text-red-700',
  };
  return map[stage] || map.lead;
}

function fmt(n) {
  return Number(n).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await contactsAPI.getProfile(id);
        setProfile(res.data);
      } catch {
        toast.error('Impossible de charger le profil');
        navigate('/contacts');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="space-y-5 max-w-4xl mx-auto">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-28 rounded-xl" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!profile) return null;

  const fullName = `${profile.first_name} ${profile.last_name}`;

  return (
    <div className="space-y-5 max-w-4xl mx-auto animate-fade-in">
      {/* Back link */}
      <Link
        to="/contacts"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft size={15} />
        Retour aux contacts
      </Link>

      {/* Hero card */}
      <div className="bg-card border border-border rounded-xl p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <Avatar className="h-16 w-16 flex-shrink-0">
          <AvatarFallback className={`text-lg font-bold ${getAvatarColor(fullName)}`}>
            {getInitials(fullName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-foreground">{fullName}</h1>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${loyaltyBadge(profile.loyalty_status)}`}>
              {profile.loyalty_status}
            </span>
          </div>
          {profile.position && (
            <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <Briefcase size={13} />
              {profile.position}
            </p>
          )}
          {profile.company && (
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Building2 size={13} />
              {profile.company}
            </p>
          )}
          <div className="flex flex-wrap gap-3 mt-2">
            {profile.email && (
              <a href={`mailto:${profile.email}`} className="flex items-center gap-1.5 text-sm text-primary hover:underline">
                <Mail size={13} />
                {profile.email}
              </a>
            )}
            {profile.phone && (
              <a href={`tel:${profile.phone}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                <Phone size={13} />
                {profile.phone}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label="Lifetime Value"
          value={fmt(profile.ltv)}
          icon={Receipt}
          color="bg-emerald-500"
        />
        <KpiCard
          label="Commandes"
          value={`${profile.total_orders} facture${profile.total_orders > 1 ? 's' : ''}`}
          icon={FileText}
          color="bg-blue-500"
        />
        <KpiCard
          label="Engagement"
          value={<span className={engagementColor(profile.engagement_score)}>{profile.engagement_score}</span>}
          icon={Activity}
          color="bg-violet-500"
        />
        <KpiCard
          label="Risque churn"
          value={<span className={churnColor(profile.churn_risk)}>{profile.churn_risk}</span>}
          icon={ShieldAlert}
          color="bg-amber-500"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="history">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="history" className="flex items-center gap-1.5">
            <Receipt size={14} /> Historique achats
          </TabsTrigger>
          <TabsTrigger value="opportunities" className="flex items-center gap-1.5">
            <TrendingUp size={14} /> Opportunités
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-1.5">
            <FileText size={14} /> Notes & Tags
          </TabsTrigger>
        </TabsList>

        {/* Historique achats */}
        <TabsContent value="history" className="mt-4 space-y-3">
          {profile.invoices?.length === 0 && profile.quotes?.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground text-sm">Aucun historique d'achat</p>
          ) : (
            <>
              {profile.invoices?.length > 0 && (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-border bg-muted/30">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Factures ({profile.invoices.length})</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">N°</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Date</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Total</th>
                          <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {profile.invoices.map(inv => (
                          <tr key={inv.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => navigate('/invoices')}>
                            <td className="px-4 py-3 font-mono text-xs text-primary hover:underline">{inv.invoice_number}</td>
                            <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{fmtDate(inv.created_at)}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmt(inv.total)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${invoiceStatusBadge(inv.status)}`}>
                                {inv.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {profile.quotes?.length > 0 && (
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-border bg-muted/30">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Devis ({profile.quotes.length})</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">N°</th>
                          <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground hidden sm:table-cell">Date</th>
                          <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Total</th>
                          <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {profile.quotes.map(q => (
                          <tr key={q.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => navigate('/quotes')}>
                            <td className="px-4 py-3 font-mono text-xs text-primary hover:underline">{q.quote_number}</td>
                            <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{fmtDate(q.created_at)}</td>
                            <td className="px-4 py-3 text-right font-semibold">{fmt(q.total)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${quoteStatusBadge(q.status)}`}>
                                {q.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Opportunités */}
        <TabsContent value="opportunities" className="mt-4">
          {profile.opportunities?.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground text-sm">Aucune opportunité liée</p>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Titre</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 hidden sm:table-cell">Valeur</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40">Étape</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 hidden md:table-cell">Clôture prévue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {profile.opportunities.map(opp => (
                      <tr key={opp.id} className="hover:bg-muted/20 cursor-pointer" onClick={() => navigate('/pipeline')}>
                        <td className="px-4 py-3 font-medium text-primary hover:underline">{opp.title}</td>
                        <td className="px-4 py-3 text-right font-semibold hidden sm:table-cell">{fmt(opp.value)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${opportunityStageColor(opp.stage)}`}>
                            {opp.stage}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground hidden md:table-cell">
                          {fmtDate(opp.expected_close_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Notes & Tags */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">Notes</h3>
            {profile.notes ? (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{profile.notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground italic">Aucune note</p>
            )}
          </div>
          {profile.tags?.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Customer DNA</h3>
              <div className="flex flex-wrap gap-2">
                {profile.tags.map(tag => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
